import { type ReactNode, useCallback, useLayoutEffect, useMemo, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useNavigate } from '@tanstack/react-router';
import { useRealmAuthContext, useRealmAuthSetActiveAccess } from '@interxfi/api-client';
import { TenantContext, useActiveRealmAPIContext } from '@interxfi/api-client/realm-context';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Button } from '@interxfi/design-system/components/button/button';
import { useMergedDialogOpenProps } from '@interxfi/design-system/components/dialog/utils';
import { TextButton } from '@interxfi/design-system/components/link/text-link/text-button';
import { useAddToast } from '@interxfi/design-system/components/toast/toast-context';
import { Typography } from '@interxfi/design-system/components/typography';
import ArrowRightIcon from '@interxfi/design-system/icons/react/icon-arrow-right';
import type { IxmUserauthV1JoinedRealmAccess } from '@interxfi/types/generated/api';
import { TenantLogomark } from '../../../features/tenant/tenant-logomark';
import { getDefaultRouteForAccess } from '../../../util/routing/defaults';
import { RealmPill } from '../../realm/realm-pill';
import styles from './switch-tenant-dialog.module.scss';

function getBestRealmAccessOption(
  activeAccessId: string | undefined,
  targetRealmId: string | undefined,
  targetTenantId: string | undefined,
  availableAccessOptions: IxmUserauthV1JoinedRealmAccess[] = [],
) {
  if (!activeAccessId || !availableAccessOptions.length) {
    return undefined;
  }

  const matchingAccess = availableAccessOptions.find(
    (access) => access.realm?.realmId === targetRealmId && access.tenant?.tenantId === targetTenantId,
  );
  if (matchingAccess) {
    return matchingAccess;
  }

  return availableAccessOptions.find((access) => access.access?.realmAccessId !== activeAccessId) || availableAccessOptions[0];
}

function getDisplayName(tenantName: string | undefined, tenantType: string | undefined) {
  if (tenantType === TenantContext.Clearing) {
    return 'Interchange Clearing';
  }

  return tenantName;
}

function formatAccessName(
  current: IxmUserauthV1JoinedRealmAccess | undefined,
  next: IxmUserauthV1JoinedRealmAccess | undefined,
  accesses: IxmUserauthV1JoinedRealmAccess[] | undefined,
  backupLabel: string,
) {
  if (!current) {
    return backupLabel;
  }

  if (current?.tenant?.tenantType === TenantContext.Organization && next?.tenant?.tenantType === TenantContext.Organization) {
    return getDisplayName(current.tenant?.data?.spec?.name, current.tenant?.tenantType);
  }

  if (current?.tenant?.tenantType === TenantContext.Platform) {
    return 'Platform Workspace';
  }

  if (
    current?.tenant?.tenantType === TenantContext.Organization &&
    !accesses?.some((access) => access.tenant?.tenantType === TenantContext.Platform)
  ) {
    return 'Admin Workspace';
  }

  return getDisplayName(current.tenant?.data?.spec?.name, current.tenant?.tenantType) || backupLabel;
}

function formatRealmPillName(realmName: string | undefined, accesses?: IxmUserauthV1JoinedRealmAccess[]) {
  switch (realmName?.toLowerCase()) {
    case 'meta':
      return accesses?.find((access) => access.tenant?.tenantType === TenantContext.Platform) ? 'Platform' : 'Admin';
    default:
      return realmName;
  }
}

interface SwitchTenantDialogProps {
  isOpen: boolean;
  onCancel?: VoidFunction;
  onConfirm?: VoidFunction;
  onOpenChange: (open: boolean) => void;
  targetRealmId: string;
  targetTenantId: string;
  trigger: ReactNode;
}

export function SwitchTenantDialog({
  isOpen: providedIsOpen,
  onCancel,
  onConfirm,
  onOpenChange: providedOnOpenChange,
  targetRealmId,
  targetTenantId,
  trigger,
}: SwitchTenantDialogProps) {
  const addToast = useAddToast();
  const [minPillWidth, setMinPillWidth] = useState<undefined | number>(undefined);
  const [currentPill, setCurrentPill] = useState<HTMLSpanElement | null>(null);
  const [targetPill, setTargetPill] = useState<HTMLSpanElement | null>(null);
  const [isOpen, onOpenChange] = useMergedDialogOpenProps({ isOpen: providedIsOpen, onOpenChange: providedOnOpenChange });
  const navigate = useNavigate();
  const setActiveRealmAccess = useRealmAuthSetActiveAccess();
  const context = useRealmAuthContext();
  const { activeRealmAccess } = useActiveRealmAPIContext();
  const accessesForRealm = useMemo(
    () => context?.accesses?.filter((access) => access.realm?.realmId === targetRealmId),
    [context?.accesses, targetRealmId],
  );

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      onOpenChange(newOpen);

      if (!newOpen) {
        onCancel?.();
      }
    },

    [onOpenChange, onCancel],
  );

  const targetAccess = useMemo(
    () => getBestRealmAccessOption(activeRealmAccess?.access?.realmAccessId, targetRealmId, targetTenantId, accessesForRealm),
    [activeRealmAccess?.access?.realmAccessId, targetRealmId, targetTenantId, accessesForRealm],
  );

  const targetAccessLabel = [
    formatAccessName(targetAccess, activeRealmAccess, context?.accesses, targetRealmId),
    formatRealmPillName(targetAccess?.realm?.data?.spec?.name, context?.accesses),
  ]
    .filter(Boolean)
    .join(' ');

  const handleTenantSwitch = useCallback(async () => {
    if (targetAccess?.access?.realmAccessId) {
      setActiveRealmAccess(targetAccess.access.realmAccessId);

      addToast({
        id: `tenantSwitchedTo${targetAccess.access.realmAccessId}`,
        content: `You've switched to ${targetAccessLabel}`,
        variant: 'success',
      });

      await navigate({
        to: getDefaultRouteForAccess(targetAccess.tenant?.tenantType as TenantContext),
      });

      onOpenChange(false);
    }

    onConfirm?.();
  }, [
    addToast,
    navigate,
    onOpenChange,
    setActiveRealmAccess,
    targetAccess?.access?.realmAccessId,
    targetAccess?.tenant?.tenantType,
    onConfirm,
    targetAccessLabel,
  ]);

  useLayoutEffect(() => {
    function handleResize() {
      setMinPillWidth(Math.max(currentPill?.getBoundingClientRect().width || 0, targetPill?.getBoundingClientRect().width || 0) || undefined);
    }

    const resizeObserver = new ResizeObserver(handleResize);

    if (currentPill) {
      resizeObserver.observe(currentPill);
    }

    if (targetPill) {
      resizeObserver.observe(targetPill);
    }

    // Manually trigger for initial state
    handleResize();

    return () => {
      resizeObserver.disconnect();
    };
  }, [currentPill, targetPill]);

  const pillStyle = { minWidth: minPillWidth };

  return (
    <DialogPrimitive.Root onOpenChange={handleOpenChange} open={isOpen}>
      <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Content className={styles.switchTenantDialog}>
          <VerticalStack align="center" className={styles.container} gap={10} inlineAlign="center">
            <DialogPrimitive.Title asChild>
              <Typography align="center" lineHeight="1.625rem" size="1.125rem" variant="heading" weight="bold">
                Leave {formatAccessName(activeRealmAccess, targetAccess, context?.accesses, 'current tenant')} and switch to{' '}
                {formatAccessName(targetAccess, activeRealmAccess, context?.accesses, targetRealmId)}?
              </Typography>
            </DialogPrimitive.Title>

            <DialogPrimitive.Description className={styles.visuallyHidden}>Change your active tenant.</DialogPrimitive.Description>

            <div className={styles.fromToContainer}>
              <div className={styles.tenantBox}>
                <HorizontalStack align="center" blockAlign="center" gap={2} wrap="nowrap">
                  <TenantLogomark
                    shouldLoadLogoMark
                    size="large"
                    tenantId={activeRealmAccess?.tenant?.tenantId}
                    tenantType={activeRealmAccess?.tenant?.tenantType}
                  />

                  <RealmPill
                    realmName={formatRealmPillName(activeRealmAccess?.realm?.data?.spec?.name, context?.accesses)}
                    ref={setCurrentPill}
                    size="large"
                    style={pillStyle}
                  />
                </HorizontalStack>
              </div>

              <ArrowRightIcon aria-hidden className={styles.arrow} width={16} />

              <div className={styles.tenantBox}>
                <HorizontalStack align="center" blockAlign="center" gap={2} wrap="nowrap">
                  <TenantLogomark
                    shouldLoadLogoMark
                    size="large"
                    tenantId={targetAccess?.tenant?.tenantId}
                    tenantType={targetAccess?.tenant?.tenantType}
                  />

                  <RealmPill
                    realmName={formatRealmPillName(targetAccess?.realm?.data?.spec?.name, context?.accesses)}
                    ref={setTargetPill}
                    size="large"
                    style={pillStyle}
                  />
                </HorizontalStack>
              </div>
            </div>

            <Button disabled={!targetAccess} fullWidth onClick={handleTenantSwitch} size="large" variant="primary">
              Yes, Switch
            </Button>

            <DialogPrimitive.Close asChild>
              <TextButton>No, Go Back</TextButton>
            </DialogPrimitive.Close>
          </VerticalStack>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
